// src/utils/backgroundSync.js
// Schedules the background calendar sync task via @capacitor/background-runner.
//
// Call scheduleBackgroundSync(userId, session) once after the user is
// authenticated. The OS will wake the app in the background and run
// background-runner.js roughly every 15 minutes (minimum iOS allows).
//
// CapacitorKV (available in the runner) and @capacitor/preferences share the
// same NSUserDefaults backing on iOS, so tokens written here are readable
// in the isolated background-runner context without any native bridging.

import { Capacitor } from '@capacitor/core';

/**
 * Store auth context for the background runner and register the sync task.
 * No-op on web.
 *
 * @param {string} userId   Supabase user UUID
 * @param {object} session  Supabase session object ({ access_token, refresh_token })
 */
export async function scheduleBackgroundSync(userId, session) {
  if (!Capacitor.isNativePlatform() || !userId) return;

  try {
    // Write tokens under known keys so background-runner.js can read them
    // via CapacitorKV.get(). Must happen before dispatchEvent so the runner
    // always has fresh credentials.
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: 'bgSync_accessToken',  value: session?.access_token  || '' });
    await Preferences.set({ key: 'bgSync_refreshToken', value: session?.refresh_token || '' });
    // Supabase URL and anon key are needed by the runner to refresh expired tokens.
    // Both are already embedded in the public JS bundle; storing them here just
    // makes them reachable from the isolated runner context.
    await Preferences.set({ key: 'bgSync_supabaseUrl', value: import.meta.env.VITE_SUPABASE_URL  || '' });
    await Preferences.set({ key: 'bgSync_anonKey',     value: import.meta.env.VITE_SUPABASE_ANON_KEY || '' });

    const { BackgroundRunner } = await import('@capacitor/background-runner');
    await BackgroundRunner.dispatchEvent({
      label: 'io.orbitapp.background',
      event: 'syncCalendars',
      details: { userId, baseUrl: import.meta.env.VITE_API_BASE || window.location.origin },
    });
  } catch (err) {
    console.warn('[bgSync] Failed to schedule background runner:', err?.message);
  }
}
